import React, { useState, useEffect } from 'react';
import { useUserDetailsApi } from '../../../hooks/useUserDetails';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import modalStyles from '../../../styles/AccountModal.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';

interface EditAgencyProps {
	agencyDetails: any;
	onAgencyUpdate: (updatedAgency: any) => void;
	onCancel: () => void;
}


const EditAgency: React.FC<EditAgencyProps> = ({ agencyDetails, onAgencyUpdate, onCancel }) => {
	const { updateAgencyDetails } = useUserDetailsApi();
	const { hasPermission } = useEnhancedAuth();
	const [companyName, setCompanyName] = useState('');
	const [tradingAsName, setTradingAsName] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (agencyDetails) {
			setCompanyName(agencyDetails.companyName || '');
			setTradingAsName(agencyDetails.tradingAsName || '');
		}
	}, [agencyDetails]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		setSubmitting(true);
		try {
			const updatedAgency = await updateAgencyDetails(agencyDetails.agencyId, {
				companyName,
				tradingAsName
			});
			onAgencyUpdate(updatedAgency);
			alert('Agency information updated successfully!');
		} catch (err: any) {
			setError(err.message || 'Failed to update agency. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};


	// Check if user has permission to edit agency
	if (!hasPermission('agency_manage_own')) {
		return (
			<div className={modalStyles.backdrop}>
				<div className={`${modalStyles.modal} ${modalStyles.modalSm}`}>
					<div className={modalStyles.header}>
						<h3 className={modalStyles.title}>Access Denied</h3>
						<button
							type="button"
							className={modalStyles.closeButton}
							onClick={onCancel}
							aria-label="Close"
						>
							×
						</button>
					</div>
					<div className={modalStyles.body}>
						<p className={modalStyles.staticValue}>You don't have permission to edit agency information.</p>
					</div>
					<div className={modalStyles.footer}>
						<button
							type="button"
							onClick={onCancel}
							className={`${buttonStyles.button} ${buttonStyles.secondary}`}
						>
							Close
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={modalStyles.backdrop}>
			<div className={`${modalStyles.modal} ${modalStyles.modalSm}`}>
				<div className={modalStyles.header}>
					<h3 className={modalStyles.title}>Edit Agency Information</h3>
					<button
						type="button"
						className={modalStyles.closeButton}
						onClick={onCancel}
						aria-label="Close"
					>
						×
					</button>
				</div>
				<form onSubmit={handleSubmit}>
					<div className={modalStyles.body}>
						<div className={modalStyles.field}>
							<label className={modalStyles.label} htmlFor="companyName">Company Name</label>
							<input
								id="companyName"
								type="text"
								value={companyName}
								onChange={e => setCompanyName(e.target.value)}
								required
								className={modalStyles.input}
							/>
						</div>
						<div className={modalStyles.field} style={{ marginTop: 18 }}>
							<label className={modalStyles.label} htmlFor="tradingAsName">Trading-As</label>
							<input
								id="tradingAsName"
								type="text"
								value={tradingAsName}
								onChange={e => setTradingAsName(e.target.value)}
								className={modalStyles.input}
							/>
						</div>
						{error && <div className={modalStyles.error}>{error}</div>}
					</div>
					<div className={modalStyles.footer}>
						<button
							type="button"
							onClick={onCancel}
							className={`${buttonStyles.button} ${buttonStyles.secondary}`}
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting}
							className={`${buttonStyles.button} ${buttonStyles.primary}`}
						>
							{submitting ? 'Updating...' : 'Update Agency'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EditAgency;